import { NotificationContentModel } from '../../../models/notification/notification-content.model'
import { DeliveryChannel } from '@pushly/models/lib/enums'
import { SharedContent } from '../../../interfaces/notification-shared-content'
import { useCallback } from 'react'

export type SharedChannelProperty = 'Title' | 'Body' | 'IconUrl' | 'ImageUrl' | 'LandingUrl'

export const useSharedChannelFieldSetter = (
    value: NotificationContentModel,
    channels: DeliveryChannel[] = [],
    onChange: (value: NotificationContentModel) => void,
) => {
    // Form Shared Fields will cascade updates to these fields for every channel enabled OR passed
    // "set" method must have same name for this shared logic to work
    // TODO: Handle Landing URL Updates (native alt url vs landing url per channel transition)
    return useCallback(
        <F extends SharedChannelProperty>(
            field: F,
            updateValue: any,
            options: {
                prevValue?: NotificationContentModel
                updateChannels?: DeliveryChannel[]
                waitToCompleteChange?: boolean
            } = {},
        ) => {
            const updateChannels = options.updateChannels
            const workingValue = options.prevValue ? options.prevValue : value.clone()

            const resolvedChannels = [...(updateChannels ?? channels)]
            if (!updateChannels) {
                resolvedChannels.push('default' as DeliveryChannel)
            }

            const setMethod: keyof SharedContent = `set${field}`

            for (const channel of resolvedChannels) {
                let content: SharedContent

                switch (channel) {
                    case DeliveryChannel.WEB:
                        content = workingValue.getWebContent().clone()
                        break
                    case DeliveryChannel.NATIVE_IOS:
                        content = workingValue.getNativeIosContent().clone()
                        break
                    case DeliveryChannel.NATIVE_ANDROID:
                        content = workingValue.getNativeAndroidContent().clone()
                        break
                    default:
                        try {
                            content = workingValue.getDefaultContent().clone()
                        } catch (error) {
                            console.error('Could not clone default content', { error, workingValue })
                            throw error
                        }
                        break
                }

                content[setMethod]!(updateValue)
                workingValue.setChannelContent(content)
            }

            if (!options.waitToCompleteChange) {
                onChange?.(workingValue)
            }

            return workingValue
        },
        [value, channels, onChange],
    )
}
